import { clinicManager } from './clinicManager';

/**
 * Permissions and Authorization Library
 * Handles all permission checks and authorization logic
 */
export class PermissionManager {
  constructor() {
    this.roles = {
      SUPER_ADMIN: 'super_admin',
      ADMIN: 'admin',
      VIEWER: 'viewer'
    };
  }

  /**
   * Check if user can manage users
   * @param {Object} currentUser - Current user object
   * @param {Object} targetUser - Target user object (optional)
   * @returns {boolean} True if user can manage users
   */
  canManageUsers(currentUser, targetUser = null) {
    if (!currentUser) return false;

    // Super admins can manage all users
    if (this.isSuperAdmin(currentUser)) {
      return true;
    }

    // Clinic admins can manage users in their clinic(s)
    if (this.isClinicAdmin(currentUser)) {
      // If no target user specified, they can generally manage users
      if (!targetUser) return true;

      // Cannot manage super admins
      if (this.isSuperAdmin(targetUser)) {
        return false;
      }

      // Determine clinics where current user is admin
      let adminClinics = [];
      if (Array.isArray(currentUser.clinicMemberships)) {
        adminClinics = currentUser.clinicMemberships
          .filter(m => m.role === 'admin' || m.role === 'super_admin')
          .map(m => m.clinicId);
      } else if (Array.isArray(currentUser.clinicAssociations)) {
        adminClinics = currentUser.clinicAssociations
          .filter(m => m.role === 'admin' || m.role === 'super_admin')
          .map(m => m.clinicId);
      } else if (currentUser.role === this.roles.ADMIN) {
        adminClinics = [currentUser.clinicId];
      }

      // Determine clinics where target user belongs
      let userClinics = [];
      if (Array.isArray(targetUser.clinicMemberships)) {
        userClinics = targetUser.clinicMemberships.map(m => m.clinicId);
      } else if (Array.isArray(targetUser.clinicAssociations)) {
        userClinics = targetUser.clinicAssociations.map(m => m.clinicId);
      } else if (Array.isArray(targetUser.clinics)) {
        userClinics = targetUser.clinics.map(m => m.clinicId);
      } else if (targetUser.clinicId) {
        userClinics = [targetUser.clinicId];
      }

      // Admin can manage user if they share at least one clinic where admin has admin role
      return adminClinics.some(clinicId => userClinics.includes(clinicId));
    }

    return false;
  }

  /**
   * Check if user can manage clinics
   * @param {Object} currentUser - Current user object
   * @returns {boolean} True if user can manage clinics
   */
  canManageClinics(currentUser) {
    return this.isSuperAdmin(currentUser);
  }

  /**
   * Check if user can assign topics to other users
   * @param {Object} currentUser - Current user object
   * @param {Object} targetUser - Target user object
   * @param {string[]} topics - Topics to assign
   * @returns {Promise<boolean>} True if user can assign these topics
   */
  async canAssignTopics(currentUser, targetUser, topics) {
    if (!currentUser || !targetUser) return false;

    // Super admins can assign any topics to anyone
    if (this.isSuperAdmin(currentUser)) {
      return true;
    }

    // Clinic admins can assign topics within clinics where they have admin role
    if (this.isClinicAdmin(currentUser)) {
      // Get clinics where current user is admin
      let adminClinicIds = [];
      if (Array.isArray(currentUser.clinicMemberships)) {
        adminClinicIds = currentUser.clinicMemberships
          .filter(m => m.role === this.roles.ADMIN || m.role === this.roles.SUPER_ADMIN)
          .map(m => m.clinicId);
      } else if (Array.isArray(currentUser.clinicAssociations)) {
        adminClinicIds = currentUser.clinicAssociations
          .filter(m => m.role === this.roles.ADMIN || m.role === this.roles.SUPER_ADMIN)
          .map(m => m.clinicId);
      } else if (currentUser.clinicId) {
        adminClinicIds = [currentUser.clinicId];
      }

      // Get clinics where target user belongs
      let targetClinicIds = [];
      if (Array.isArray(targetUser.clinicMemberships)) {
        targetClinicIds = targetUser.clinicMemberships.map(m => m.clinicId);
      } else if (Array.isArray(targetUser.clinicAssociations)) {
        targetClinicIds = targetUser.clinicAssociations.map(m => m.clinicId);
      } else if (Array.isArray(targetUser.clinics)) {
        targetClinicIds = targetUser.clinics.map(m => m.clinicId);
      } else if (targetUser.clinicId) {
        targetClinicIds = [targetUser.clinicId];
      }

      // Must share at least one clinic where current user is admin
      const sharedClinic = adminClinicIds.find(id => targetClinicIds.includes(id));
      if (!sharedClinic) {
        return false;
      }

      // Check if all requested topics are available for the shared clinic
      try {
        const availableTopics = await clinicManager.getClinicTopics(sharedClinic);
        return topics.every(topic => availableTopics.includes(topic));
      } catch (error) {
        console.error('Error checking available topics:', error);
        return false;
      }
    }

    return false;
  }

  /**
   * Check if user can access reports
   * @param {Object} currentUser - Current user object
   * @param {string} reportClinicId - Clinic ID of the report
   * @param {string} reportTopic - Topic of the report
   * @returns {boolean} True if user can access the report
   */
  canAccessReport(currentUser, reportClinicId, reportTopic) {
    if (!currentUser) return false;

    // Super admins can access all reports
    if (this.isSuperAdmin(currentUser)) {
      return true;
    }

    // Get all clinic IDs the user has access to
    let userClinicIds = [];
    if (Array.isArray(currentUser.clinicMemberships)) {
      userClinicIds = currentUser.clinicMemberships.map(m => m.clinicId);
    } else if (Array.isArray(currentUser.clinicAssociations)) {
      userClinicIds = currentUser.clinicAssociations.map(m => m.clinicId);
    } else if (currentUser.clinicId) {
      userClinicIds = [currentUser.clinicId];
    }

    // Users can only access reports from their clinic(s)
    if (!userClinicIds.includes(reportClinicId)) {
      return false;
    }

    // Check if user has access to this topic
    if (!currentUser.topics || !Array.isArray(currentUser.topics)) {
      return false;
    }

    return currentUser.topics.includes(reportTopic);
  }

  /**
   * Check if user can create/edit clinic configurations
   * @param {Object} currentUser - Current user object
   * @param {string} clinicId - Clinic ID being configured
   * @returns {boolean} True if user can configure this clinic
   */
  canConfigureClinic(currentUser, clinicId) {
    if (!currentUser) return false;

    // Super admins can configure any clinic
    if (this.isSuperAdmin(currentUser)) {
      return true;
    }

    // Clinic admins can only configure clinics where they are admin
    if (this.isClinicAdmin(currentUser)) {
      // Check multi-clinic memberships first
      if (Array.isArray(currentUser.clinicMemberships)) {
        return currentUser.clinicMemberships.some(
          m => m.clinicId === clinicId && (m.role === 'admin' || m.role === 'super_admin')
        );
      }
      if (Array.isArray(currentUser.clinicAssociations)) {
        return currentUser.clinicAssociations.some(
          m => m.clinicId === clinicId && (m.role === 'admin' || m.role === 'super_admin')
        );
      }
      // Fallback to legacy single clinic check
      return currentUser.clinicId === clinicId;
    }

    return false;
  }

  /**
   * Check if user can delete users
   * @param {Object} currentUser - Current user object
   * @param {Object} targetUser - Target user to delete
   * @returns {boolean} True if user can delete the target user
   */
  canDeleteUser(currentUser, targetUser) {
    if (!currentUser || !targetUser) return false;

    // Cannot delete yourself (check multiple identifiers for safety)
    if ((currentUser.username && currentUser.username === targetUser.username) ||
      (currentUser.email && currentUser.email === targetUser.email) ||
      (currentUser.cognitoSub && (currentUser.cognitoSub === targetUser.cognitoSub || currentUser.cognitoSub === targetUser.userId))) {
      return false;
    }

    // Super admins can delete any user (self-delete already guarded above)
    if (this.isSuperAdmin(currentUser)) {
      return true;
    }

    // Clinic admins can delete users in their clinic(s) (except super admins)
    if (this.isClinicAdmin(currentUser)) {
      // Cannot delete super admins
      if (this.isSuperAdmin(targetUser)) {
        return false;
      }

      // Check if target user is in any of the admin's clinics
      let adminClinics = [];
      if (Array.isArray(currentUser.clinicMemberships)) {
        adminClinics = currentUser.clinicMemberships.filter(m => m.role === 'admin' || m.role === 'super_admin').map(m => m.clinicId);
      } else if (Array.isArray(currentUser.clinicAssociations)) {
        adminClinics = currentUser.clinicAssociations.filter(m => m.role === 'admin' || m.role === 'super_admin').map(m => m.clinicId);
      } else if (Array.isArray(currentUser.clinics)) {
        adminClinics = currentUser.clinics.filter(m => m.role === 'admin' || m.role === 'super_admin').map(m => m.clinicId);
      } else if (currentUser.clinicId) {
        adminClinics = [currentUser.clinicId];
      }

      let userClinics = [];
      if (Array.isArray(targetUser.clinicMemberships)) {
        userClinics = targetUser.clinicMemberships.map(m => m.clinicId);
      } else if (Array.isArray(targetUser.clinicAssociations)) {
        userClinics = targetUser.clinicAssociations.map(m => m.clinicId);
      } else if (Array.isArray(targetUser.clinics)) {
        userClinics = targetUser.clinics.map(m => m.clinicId);
      } else if (targetUser.clinicId) {
        userClinics = [targetUser.clinicId];
      }

      // Admin can only fully delete a user if they manage ALL of the target's clinics
      return userClinics.length > 0 && userClinics.every(clinicId => adminClinics.includes(clinicId));
    }

    return false;
  }

  /**
   * Check if user can manage invitations
   * @param {Object} currentUser - Current user object
   * @returns {boolean} True if user can manage invitations
   */
  canManageInvites(currentUser) {
    if (!currentUser) return false;
    return this.isSuperAdmin(currentUser) || this.isClinicAdmin(currentUser);
  }

  /**
   * Check if user can view admin panel
   * @param {Object} currentUser - Current user object
   * @returns {boolean} True if user can view admin panel
   */
  canViewAdminPanel(currentUser) {
    if (!currentUser) return false;
    return this.isSuperAdmin(currentUser) || this.isClinicAdmin(currentUser);
  }

  /**
   * Get available actions for a user
   * @param {Object} currentUser - Current user object
   * @param {Object} contextUser - Context user (optional)
   * @returns {Object} Available actions object
   */
  getAvailableActions(currentUser, contextUser = null) {
    const actions = {
      // User management
      canManageUsers: this.canManageUsers(currentUser),
      canCreateUsers: this.canManageUsers(currentUser),
      canEditUsers: this.canManageUsers(currentUser, contextUser),
      canDeleteUsers: this.canManageUsers(currentUser, contextUser),
      canAssignRoles: this.canManageUsers(currentUser, contextUser),

      // Clinic management
      canManageClinics: this.canManageClinics(currentUser),
      canConfigureClinics: this.canManageClinics(currentUser),
      canCreateClinics: this.canManageClinics(currentUser),
      canDeleteClinics: this.canManageClinics(currentUser),

      // Admin panel access
      canViewAdminPanel: this.canViewAdminPanel(currentUser),
      canManageInvites: this.canManageInvites(currentUser),
      canViewUsersList: this.canManageUsers(currentUser),
      canViewClinicsList: this.canManageClinics(currentUser),

      // Role information
      isSuperAdmin: this.isSuperAdmin(currentUser),
      isClinicAdmin: this.isClinicAdmin(currentUser),
      isViewer: this.isViewer(currentUser)
    };

    return actions;
  }

  /**
   * Filter reports based on user permissions
   * @param {Object} currentUser - Current user object
   * @param {Object[]} reports - Array of report objects
   * @returns {Object[]} Filtered reports array
   */
  filterReports(currentUser, reports) {
    if (!currentUser || !Array.isArray(reports)) {
      return [];
    }

    return reports.filter(report => {
      return this.canAccessReport(currentUser, report.clinicId, report.topic);
    });
  }

  /**
   * Filter users based on current user's permissions
   * @param {Object} currentUser - Current user object
   * @param {Object[]} users - Array of user objects
   * @returns {Object[]} Filtered users array
   */
  filterUsers(currentUser, users) {
    if (!currentUser || !Array.isArray(users)) {
      return [];
    }

    // Super admins can see all users
    if (this.isSuperAdmin(currentUser)) {
      return users;
    }

    // Clinic admins can see users in their clinic
    if (this.isClinicAdmin(currentUser)) {
      // Determine all clinics where current user is admin/super_admin
      let adminClinicIds = [];
      if (Array.isArray(currentUser.clinicMemberships)) {
        adminClinicIds = currentUser.clinicMemberships
          .filter(m => m.role === this.roles.ADMIN || m.role === this.roles.SUPER_ADMIN)
          .map(m => m.clinicId);
      } else if (Array.isArray(currentUser.clinicAssociations)) {
        adminClinicIds = currentUser.clinicAssociations
          .filter(m => m.role === this.roles.ADMIN || m.role === this.roles.SUPER_ADMIN)
          .map(m => m.clinicId);
      } else if (currentUser.role === this.roles.ADMIN && currentUser.clinicId) {
        adminClinicIds = [currentUser.clinicId];
      }

      // Keep users who belong to any of those clinics
      return users.filter(user => {
        // Prefer structured memberships
        if (Array.isArray(user.clinicMemberships) && user.clinicMemberships.length > 0) {
          const userClinicIds = user.clinicMemberships.map(m => m.clinicId);
          return adminClinicIds.some(id => userClinicIds.includes(id));
        }
        // Support Users-table records with `clinics` array
        if (Array.isArray(user.clinics) && user.clinics.length > 0) {
          const userClinicIds = user.clinics.map(m => m.clinicId);
          return adminClinicIds.some(id => userClinicIds.includes(id));
        }
        // Fallback for flat records
        return user.clinicId ? adminClinicIds.includes(user.clinicId) : false;
      });
    }

    // Viewers can only see themselves
    return users.filter(user => user.username === currentUser.username);
  }

  /**
   * Get topics a user can assign to others
   * @param {Object} currentUser - Current user object
   * @returns {Promise<string[]>} Array of assignable topics
   */
  async getAssignableTopics(currentUser) {
    if (!currentUser) return [];

    try {
      // Super admins can assign any global topic
      if (this.isSuperAdmin(currentUser)) {
        return await clinicManager.getGlobalTopics();
      }

      // Clinic admins can assign topics available to their clinic
      if (this.isClinicAdmin(currentUser) && currentUser.clinicId !== 'unassigned') {
        return await clinicManager.getClinicTopics(currentUser.clinicId);
      }

      return [];
    } catch (error) {
      console.error('Error getting assignable topics:', error);
      return [];
    }
  }

  /**
   * Check if user is super admin
   * @param {Object} user - User object
   * @returns {boolean} True if user is super admin
   */
  isSuperAdmin(user) {
    // Legacy single-role check
    if (user?.role === this.roles.SUPER_ADMIN) {
      return true;
    }

    // Multi-clinic check: user is super admin if they have super_admin role in any clinic
    if (Array.isArray(user?.clinicMemberships)) {
      if (user.clinicMemberships.some(m => m.role === this.roles.SUPER_ADMIN)) return true;
    }
    if (Array.isArray(user?.clinicAssociations)) {
      if (user.clinicAssociations.some(m => m.role === this.roles.SUPER_ADMIN)) return true;
    }
    // DynamoDB records use `clinics` array
    if (Array.isArray(user?.clinics)) {
      if (user.clinics.some(m => m.role === this.roles.SUPER_ADMIN)) return true;
    }

    return false;
  }

  /**
   * Check if user is clinic admin
   * @param {Object} user - User object
   * @returns {boolean} True if user is clinic admin
   */
  isClinicAdmin(user) {
    // Legacy single-role check
    if (user?.role === this.roles.ADMIN) {
      return true;
    }

    // Multi-clinic check: user is admin if they have admin role in any clinic
    if (Array.isArray(user?.clinicMemberships)) {
      if (user.clinicMemberships.some(m => m.role === this.roles.ADMIN || m.role === this.roles.SUPER_ADMIN)) return true;
    }
    if (Array.isArray(user?.clinicAssociations)) {
      if (user.clinicAssociations.some(m => m.role === this.roles.ADMIN || m.role === this.roles.SUPER_ADMIN)) return true;
    }
    // DynamoDB records use `clinics` array
    if (Array.isArray(user?.clinics)) {
      if (user.clinics.some(m => m.role === this.roles.ADMIN || m.role === this.roles.SUPER_ADMIN)) return true;
    }

    return false;
  }

  /**
   * Check if user is viewer (has only viewer-level permissions, not admin anywhere)
   * @param {Object} user - User object
   * @returns {boolean} True if user is viewer only
   */
  isViewer(user) {
    // If user is super admin or clinic admin anywhere, they're not a viewer
    if (this.isSuperAdmin(user) || this.isClinicAdmin(user)) {
      return false;
    }

    // Legacy single-role check
    if (user?.role === this.roles.VIEWER) {
      return true;
    }

    // Multi-clinic check: user is viewer if they have no admin/super_admin roles in any clinic
    if (Array.isArray(user?.clinicMemberships) && user.clinicMemberships.length > 0) {
      return user.clinicMemberships.every(m => m.role === this.roles.VIEWER || m.role === 'operator');
    }
    if (Array.isArray(user?.clinicAssociations) && user.clinicAssociations.length > 0) {
      return user.clinicAssociations.every(m => m.role === this.roles.VIEWER || m.role === 'operator');
    }

    // Default to viewer if no role information
    return true;
  }

  /**
   * Get user's effective permissions summary
   * @param {Object} user - User object
   * @returns {Object} Permissions summary
   */
  getUserPermissionsSummary(user) {
    if (!user) {
      return {
        role: 'none',
        clinic: 'none',
        topics: [],
        canManageUsers: false,
        canManageClinics: false,
        canAccessReports: false
      };
    }

    return {
      role: user.role || 'viewer',
      clinic: user.clinicId || 'unassigned',
      topics: user.topics || [],
      canManageUsers: this.canManageUsers(user),
      canManageClinics: this.canManageClinics(user),
      canAccessReports: (user.topics || []).length > 0,
      isSuperAdmin: this.isSuperAdmin(user),
      isClinicAdmin: this.isClinicAdmin(user),
      isViewer: this.isViewer(user)
    };
  }

  /**
   * Validate permission context
   * @param {Object} currentUser - Current user object
   * @param {string} action - Action being attempted
   * @param {Object} context - Additional context (targetUser, clinicId, etc.)
   * @returns {Object} Validation result
   */
  validatePermission(currentUser, action, context = {}) {
    const result = {
      allowed: false,
      reason: null
    };

    if (!currentUser) {
      result.reason = 'No authenticated user';
      return result;
    }

    switch (action) {
      case 'manage_users':
        result.allowed = this.canManageUsers(currentUser, context.targetUser);
        if (!result.allowed) {
          result.reason = 'Insufficient permissions to manage users';
        }
        break;

      case 'manage_clinics':
        result.allowed = this.canManageClinics(currentUser);
        if (!result.allowed) {
          result.reason = 'Only super admins can manage clinics';
        }
        break;

      case 'access_report':
        result.allowed = this.canAccessReport(currentUser, context.clinicId, context.topic);
        if (!result.allowed) {
          result.reason = 'No access to this report topic or clinic';
        }
        break;

      case 'view_admin_panel':
        result.allowed = this.canViewAdminPanel(currentUser);
        if (!result.allowed) {
          result.reason = 'Admin privileges required';
        }
        break;

      default:
        result.reason = `Unknown action: ${action}`;
    }

    return result;
  }
}

// Export singleton instance
export const permissionManager = new PermissionManager();

// Compatibility wrapper functions expected by unit tests
export const canManageUsers = (currentUser, targetUser = null) => {
  if (!currentUser) return false;
  const groups = currentUser.groups || [];
  const isSuper = Array.isArray(groups) && groups.includes('super_admin');
  if (isSuper) {
    // Cannot manage self when changing roles
    if (targetUser && (targetUser.id && currentUser.id && targetUser.id === currentUser.id)) return false;
    return true;
  }
  // Delegate to manager for clinic admins/viewers
  // Prevent managing self explicitly
  if (targetUser && ((currentUser.id && targetUser.id && currentUser.id === targetUser.id) || (currentUser.username && targetUser.username && currentUser.username === targetUser.username))) {
    return false;
  }
  return permissionManager.canManageUsers(currentUser, targetUser);
}

export const canManageClinic = (currentUser, clinicId) => {
  if (!currentUser) return false;
  if (permissionManager.isSuperAdmin(currentUser) || (currentUser?.groups || []).includes('super_admin')) return true;
  if (permissionManager.isClinicAdmin(currentUser)) {
    return currentUser.clinicId === clinicId;
  }
  return false;
}

export const canAccessReport = (currentUser, report) => {
  if (!currentUser || !report) return false;
  // Use assignedTopics if present (test schema), otherwise topics
  const topics = Array.isArray(currentUser.assignedTopics)
    ? currentUser.assignedTopics
    : Array.isArray(currentUser.topics) ? currentUser.topics : [];
  if (permissionManager.isSuperAdmin(currentUser) || (currentUser?.groups || []).includes('super_admin')) return true;
  if (currentUser.clinicId !== report.clinicId) return false;
  return topics.includes(report.topic);
}

export const checkReportAccess = (currentUser, report) => {
  if (!currentUser) return { allowed: false, reason: 'No authenticated user' };
  if (permissionManager.isSuperAdmin(currentUser)) {
    return { allowed: true, reason: 'Super admin access' };
  }
  if (currentUser.clinicId !== report.clinicId) {
    return { allowed: false, reason: 'User cannot access reports from different clinic' };
  }
  const topics = Array.isArray(currentUser.assignedTopics)
    ? currentUser.assignedTopics
    : Array.isArray(currentUser.topics) ? currentUser.topics : [];
  if (!topics.includes(report.topic)) {
    return { allowed: false, reason: 'User does not have access to this topic' };
  }
  return { allowed: true, reason: 'User has access to topic in their clinic' };
}

export const canAssignTopic = (currentUser, targetUser, topicsToAssign, clinicAvailableTopics = []) => {
  if (!currentUser || !targetUser) return false;
  if (permissionManager.isSuperAdmin(currentUser)) return true;
  if (!permissionManager.isClinicAdmin(currentUser)) return false;

  // Get clinics where current user is admin
  let adminClinicIds = [];
  if (Array.isArray(currentUser.clinicMemberships)) {
    adminClinicIds = currentUser.clinicMemberships
      .filter(m => m.role === 'admin' || m.role === 'super_admin')
      .map(m => m.clinicId);
  } else if (Array.isArray(currentUser.clinicAssociations)) {
    adminClinicIds = currentUser.clinicAssociations
      .filter(m => m.role === 'admin' || m.role === 'super_admin')
      .map(m => m.clinicId);
  } else if (currentUser.clinicId) {
    adminClinicIds = [currentUser.clinicId];
  }

  // Get clinics where target user belongs
  let targetClinicIds = [];
  if (Array.isArray(targetUser.clinicMemberships)) {
    targetClinicIds = targetUser.clinicMemberships.map(m => m.clinicId);
  } else if (Array.isArray(targetUser.clinicAssociations)) {
    targetClinicIds = targetUser.clinicAssociations.map(m => m.clinicId);
  } else if (Array.isArray(targetUser.clinics)) {
    targetClinicIds = targetUser.clinics.map(m => m.clinicId);
  } else if (targetUser.clinicId) {
    targetClinicIds = [targetUser.clinicId];
  }

  // Must share at least one clinic where current user is admin
  const hasSharedClinic = adminClinicIds.some(id => targetClinicIds.includes(id));
  if (!hasSharedClinic) return false;

  return (topicsToAssign || []).every(t => clinicAvailableTopics.includes(t));
}

export const validateUserPermissions = (user) => {
  const availableTopics = Array.isArray(user?.assignedTopics)
    ? user.assignedTopics
    : Array.isArray(user?.topics) ? user.topics : [];
  const isSuperAdmin = permissionManager.isSuperAdmin(user) || (user?.groups || []).includes('super_admin');
  const canManageUsersFlag = canManageUsers(user);
  const canManageOwnClinic = isSuperAdmin || permissionManager.isClinicAdmin(user);
  return {
    canManageUsers: canManageUsersFlag,
    canManageOwnClinic,
    canManageAllClinics: !!isSuperAdmin,
    canAccessReports: availableTopics.length > 0,
    canAccessAllReports: !!isSuperAdmin,
    availableTopics,
    isSuperAdmin: !!isSuperAdmin
  };
}

export const hasPermission = (user, permission, context = {}) => {
  switch (permission) {
    case 'MANAGE_USERS':
      if (!user) return false;
      if (permissionManager.isSuperAdmin(user)) return true;
      if (!permissionManager.isClinicAdmin(user)) return false;
      if (!context?.targetClinicId) return false;
      return user.clinicId === context.targetClinicId;
    case 'MANAGE_ALL_CLINICS':
      return permissionManager.isSuperAdmin(user);
    case 'MANAGE_OWN_CLINIC':
      return permissionManager.isClinicAdmin(user) || permissionManager.isSuperAdmin(user);
    case 'ACCESS_CLINIC_REPORTS':
      return Array.isArray(user?.assignedTopics) ? user.assignedTopics.length > 0 : Array.isArray(user?.topics) && user.topics.length > 0;
    case 'ASSIGN_TOPICS':
      return permissionManager.isSuperAdmin(user) || permissionManager.isClinicAdmin(user);
    default:
      return false;
  }
}

export const getEffectivePermissions = (user) => {
  const perms = [];
  if (hasPermission(user, 'MANAGE_USERS', { targetClinicId: user?.clinicId })) perms.push('MANAGE_CLINIC_USERS');
  if (hasPermission(user, 'ACCESS_CLINIC_REPORTS')) perms.push('ACCESS_CLINIC_REPORTS');
  if (hasPermission(user, 'ASSIGN_TOPICS')) perms.push('ASSIGN_TOPICS');
  if (hasPermission(user, 'MANAGE_ALL_CLINICS')) perms.push('MANAGE_ALL_CLINICS');
  return perms;
}

// Note: Avoid CommonJS exports in ESM browser bundle to prevent runtime errors
